import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { firstValueFrom } from 'rxjs';
import { ButtonComponent } from '../ui/button/button.component';
import { InterestPartyItem } from '../../models/trip-interest.types';
import { TripInterestService } from '../../services/trip-interest.service';
import { TripService } from '../../services/trip.service';
import { ConfirmationService } from '../../services/confirmation.service';

interface InterestPartyRow extends InterestPartyItem {
  fullName: string;
  converted: boolean;
}

/**
 * Interested-party cards with one-click "Add to roster". Hosts own the
 * loading, error, and empty states; this renders nothing when there are no rows.
 */
@Component({
  selector: 'app-interested-parties-list',
  imports: [TranslocoDirective, ButtonComponent, DatePipe],
  templateUrl: './interested-parties-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterestedPartiesListComponent {
  private readonly tripInterest = inject(TripInterestService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly confirmation = inject(ConfirmationService);

  /** When true, hides people already added to the roster. */
  pendingOnly = input(false);

  readonly convertingId = signal<number | null>(null);
  readonly dismissingId = signal<number | null>(null);
  /** One row action at a time. */
  readonly busy = computed(() => this.convertingId() !== null || this.dismissingId() !== null);

  readonly rows = computed((): InterestPartyRow[] => {
    // value() throws while the resource is in its error state; the host shows that error.
    const parties = this.tripInterest.parties.hasValue() ? this.tripInterest.parties.value() : [];
    return parties
      .map((party) => ({
        ...party,
        fullName: `${party.firstname} ${party.lastname}`.trim(),
        converted: party.convertedAt !== null,
      }))
      .filter((row) => !this.pendingOnly() || !row.converted);
  });

  async dismiss(row: InterestPartyRow): Promise<void> {
    const tripId = this.tripService.currentTripId();
    if (tripId === null || row.converted || this.busy()) {
      return;
    }
    const confirmed = await this.confirmation.confirm({
      header: this.transloco.translate(marker('group-management.interested-parties.dismiss-confirm-title')),
      message: this.transloco.translate(
        marker('group-management.interested-parties.dismiss-confirm-message'),
        { name: row.fullName },
      ),
      cancelText: this.transloco.translate(marker('group-management.interested-parties.dismiss-cancel')),
      confirmText: this.transloco.translate(marker('group-management.interested-parties.dismiss')),
    });
    if (!confirmed) {
      return;
    }
    this.dismissingId.set(row.id);
    try {
      await firstValueFrom(this.tripInterest.dismiss(tripId, row.id));
    } catch {
      // The list may be stale (e.g. someone else added them to the roster); refresh it.
      this.tripInterest.parties.reload();
      const toast = await this.toastController.create({
        message: this.transloco.translate(
          marker('group-management.interested-parties.dismiss-error'),
          { name: row.fullName },
        ),
        duration: 4000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    } finally {
      this.dismissingId.set(null);
    }
  }

  async convert(row: InterestPartyRow): Promise<void> {
    const tripId = this.tripService.currentTripId();
    if (tripId === null || row.converted || this.busy()) {
      return;
    }
    this.convertingId.set(row.id);
    try {
      await firstValueFrom(this.tripInterest.convert(tripId, row.id));
      const toast = await this.toastController.create({
        message: this.transloco.translate(
          marker('group-management.interested-parties.convert-success'),
          { name: row.fullName },
        ),
        duration: 3000,
        position: 'bottom',
        color: 'success',
      });
      await toast.present();
    } catch {
      // The list may be stale (e.g. already added or dismissed elsewhere); refresh it.
      this.tripInterest.parties.reload();
      const toast = await this.toastController.create({
        message: this.transloco.translate(
          marker('group-management.interested-parties.convert-error'),
          { name: row.fullName },
        ),
        duration: 4000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    } finally {
      this.convertingId.set(null);
    }
  }
}
